<template>
  <div class="masiva-table">
    <div class="masiva-table__toolbar">
      <el-radio-group
        v-if="esResultado"
        data-test="filtro-resultado"
        :model-value="store.filtro"
        @change="store.setFiltro"
      >
        <el-radio-button value="todos">Todos</el-radio-button>
        <el-radio-button value="generados">Generados exitosamente</el-radio-button>
        <el-radio-button value="no_procesados">No procesados</el-radio-button>
      </el-radio-group>
      <el-switch
        v-else
        data-test="solo-errores"
        :model-value="store.soloErrores"
        active-text="Solo errores"
        @change="store.setSoloErrores"
      />

      <div
        v-if="esResultado"
        class="masiva-table__downloads"
      >
        <el-button
          data-test="descargar-completo"
          type="primary"
          plain
          :icon="IconDownload"
          @click="exportarResultadoCompleto(store.filasResultado)"
          >Descargar resultado completo</el-button
        >
        <el-button
          v-if="store.filasNoProcesadas.length"
          data-test="descargar-no-procesados"
          type="warning"
          plain
          :icon="IconDownload"
          @click="exportarNoProcesados(store.filasResultado)"
          >Descargar no procesados</el-button
        >
      </div>
      <el-button
        v-else-if="store.hayErrores"
        data-test="exportar-errores"
        type="warning"
        plain
        :icon="IconDownload"
        @click="exportarErrores(store.filas)"
        >Exportar filas con error</el-button
      >
    </div>

    <el-table
      :data="filasTabla"
      border
      max-height="520"
      empty-text="Sin datos"
    >
      <el-table-column
        prop="numeroFila"
        label="Fila Excel"
        width="90"
      />
      <el-table-column
        v-if="esResultado"
        label="Nº Lote"
        width="90"
        align="center"
      >
        <template #default="{ row }">{{ row.numeroLote ?? '-' }}</template>
      </el-table-column>
      <el-table-column
        v-if="esResultado"
        label="Nº Cotización"
        width="130"
        align="center"
      >
        <template #default="{ row }">{{ row.codigoCotizacion ?? '-' }}</template>
      </el-table-column>
      <el-table-column
        prop="codigoProducto"
        label="Código"
        min-width="110"
        show-overflow-tooltip
      />
      <el-table-column
        label="ET"
        width="80"
      >
        <template #default="{ row }">{{ row.datosET?.codigoET ?? '-' }}</template>
      </el-table-column>
      <el-table-column
        label="Cliente"
        min-width="150"
        show-overflow-tooltip
      >
        <template #default="{ row }">{{ row.datosET?.cliente ?? '-' }}</template>
      </el-table-column>
      <el-table-column
        label="Vendedor"
        min-width="130"
        show-overflow-tooltip
      >
        <template #default="{ row }">{{ row.datosET?.vendedor ?? '-' }}</template>
      </el-table-column>
      <el-table-column
        prop="unidadMedida"
        label="UoM"
        width="90"
      />
      <el-table-column
        prop="moneda"
        label="Moneda"
        width="90"
      />
      <el-table-column
        label="Cantidades"
        min-width="140"
        show-overflow-tooltip
      >
        <template #default="{ row }">{{ formatCantidades(row.cantidades) }}</template>
      </el-table-column>
      <el-table-column
        label="Estado"
        width="150"
        align="center"
      >
        <template #default="{ row }">
          <el-tag :type="estadoTag(row).type">{{ estadoTag(row).label }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column
        :label="esResultado ? 'Detalle' : 'Detalle de error'"
        min-width="260"
        show-overflow-tooltip
      >
        <template #default="{ row }">{{ detalle(row) }}</template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { formatNumberLocale } from '@/shared/utils/locale_number_utils.js';
import { useCotizacionMasivaStore } from '../stores/cotizacion_masiva_store.js';
import {
  exportarErrores,
  exportarNoProcesados,
  exportarResultadoCompleto,
} from '../utils/cotizacion_masiva_export.js';
import IconDownload from '~icons/mdi/download';

const store = useCotizacionMasivaStore();

const esResultado = computed(() => store.fase === 'resultado');
const filasTabla = computed(() =>
  esResultado.value ? store.filasResultadoVisibles : store.filasVisibles,
);

const ESTADOS_FINALES = {
  COTIZADA: { type: 'success', label: 'Cotizada' },
  ERROR_CALCULO: { type: 'danger', label: 'Error de cálculo' },
  ERROR_VALIDACION: { type: 'danger', label: 'Error de validación' },
  NO_PROCESADA: { type: 'info', label: 'No procesada' },
};

const estadoTag = (row) => {
  if (esResultado.value) return ESTADOS_FINALES[row.estadoFinal] ?? ESTADOS_FINALES.NO_PROCESADA;
  return row.estado === 'ERROR'
    ? { type: 'danger', label: 'Error' }
    : { type: 'success', label: 'Correcta' };
};

const formatCantidades = (cantidades = []) =>
  cantidades.map((cantidad) => formatNumberLocale(cantidad, 0)).join(', ');

const detalleError = (row) => (row.errores ?? []).map((error) => error.mensaje).join('; ');
const detalle = (row) => (esResultado.value && row.errorProceso) || detalleError(row);
</script>

<style scoped>
.masiva-table {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.masiva-table__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.masiva-table__downloads {
  display: flex;
  gap: 8px;
}
</style>
